import React from 'react';
import { useDispatch } from 'react-redux';
import { withTheme } from '@emotion/react';
import { compact, head, map, uniq } from 'lodash';
import { TestId } from '@components/test-ids';
import { breakpoints } from '@styles/breakpoints';
import { TourDetails } from '@model/tours';
import { TourProductTile } from './TourProductTile';
import { CMSDestination } from '@model/contentful';
import { useI18NextContext, useResize } from '@components/hooks';
import { Page, TripTypes } from '@model/common';
import { FiltersSideBarType } from '@model/filters';
import { Carousel, getSlideDisplayCount } from '@components/common';
import { getImageUrl } from '@util/images';
import { Logging } from '@util/logging';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg';
import { getSubtitle } from '@components/recently-viewed/card/ToursRecentlyViewedCard';
import { resetSelectedFilters } from '@state/slices/filters/filterSlice';
import { clearToursByType } from '@state/slices/search/tours';
import { unsetLeadInTourReferences } from '@state/slices/tours/tour-references';

/* ***************** *
 *       Types       *
 * ***************** */

export interface TourProductGroupProps {
  testId?: string;
  cmsConfig: any;
  componentData: Array<TourDetails>;
  destination?: CMSDestination;
  tripType: TripTypes;
  path?: string;
}

/* ******************************* *
 *    TourProductGroupComponent    *
 * ******************************* */

export const TourProductGroupComponent = ({
  cmsConfig: { title, subTitle, tours, ctaLabel, ctaUrl, hideCta },
  testId,
  componentData,
  tripType,
  destination,
  path
}: TourProductGroupProps) => {
  const t: any = useI18NextContext();
  const dispatch = useDispatch();

  if (!componentData?.length) {
    Logging.error({
      text: `[TourProductGroupComponent] Component did not receive the componentData and cannot render.`
    });
    return null;
  }

  const componentTripType = head(uniq(compact(map(componentData, 'tripType')))) || tripType;
  const linkTripType = tripType === TripTypes.PRIVATE_TOURS ? tripType : componentTripType;

  /* *** LOGIC *** */
  const getTileImageSize: () => number = () => {
    switch (tours.length) {
      case 1:
        return breakpoints.large;
      case 2:
        return breakpoints.medium;
      default:
        return breakpoints.small;
    }
  };

  const resize = useResize();
  const slideCount = getSlideDisplayCount(resize, tours.length);
  const sizes = `${Math.round(100 / slideCount)}vw`;

  /* *** RENDERERS *** */
  const renderTours = () =>
    componentData.map(({ name, price, image, path, days }: TourDetails) => {
      return (
        <TourProductTile
          key={name}
          name={name}
          imageUrl={getImageUrl(image, getTileImageSize())}
          path={path}
          tripType={linkTripType}
          price={price}
          subTitle={getSubtitle(days || 0, componentTripType, t)}
          sizes={sizes}
        />
      );
    });

  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const allToursLink: LinkItem | undefined = ctaUrl
    ? undefined
    : linkBuilder.getDestinationLink(destination?.destinationPath?.fields?.path || path, linkTripType, Page.ALL_TOURS);

  const handleCtaClick = () => {
    dispatch(resetSelectedFilters(FiltersSideBarType.TOURS));
    dispatch(clearToursByType(tripType));
    dispatch(unsetLeadInTourReferences());
  };
  const ctaLabelKey =
    componentTripType === TripTypes.MULTI_CENTRE
      ? 'tour-product-group__cta--multicentre-label'
      : 'tour-product-group__cta--default-label';

  const label = ctaLabel || t(ctaLabelKey, { destination: destination?.title });
  const linkHref = ctaUrl ? `${/^\//.test(ctaUrl) ? '' : '/'}${ctaUrl}` : undefined;
  const link =
    !hideCta && (!!destination || path)
      ? { label, onClick: handleCtaClick, href: linkHref, linkItem: allToursLink }
      : undefined;

  return (
    <Carousel testId={testId || TestId.tourProductTile.group} title={title} subTitle={subTitle} link={link}>
      {renderTours()}
    </Carousel>
  );
};

export const TourProductGroup = withTheme(TourProductGroupComponent);
